import {ShoppingCartOutlined, UserOutlined} from "@ant-design/icons";
import {Segmented} from "antd";
import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {
  setContextSwitching,
  setUserContext,
  UserContext,
} from "../../redux/user-context/user-context-slice";
import {AppDispatch, RootState} from "../../redux/store";
import {switchUserContext} from "../../redux/auth/login/user-login-slice";
import {StyledContextSwitch} from "./context-switch.style";
import {CreateCoachAccountModal} from "./create-coach-account-modal";
import {UserGroupsIcon} from "../icons/user-groups-icon";
import {useQueryClient} from "@tanstack/react-query";
import {CreateCustomerAccountModal} from "./create-customer-account-modal";

const ContextSwitch = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const {userInfo} = useSelector((state: RootState) => state.userLogin);

  const {activeContext} = useSelector((state: RootState) => state.userContext);
  const [showModal, setShowModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);

  useEffect(() => {
    if (!activeContext && userInfo) {
      const defaultContext: UserContext = userInfo.isCustomer ? "customer" : "coach";
      dispatch(setUserContext(defaultContext));
    }
  }, [activeContext, userInfo, dispatch]);

  //todo: put these strings in a constant file
  const currentUserContext: UserContext =
    activeContext || (userInfo?.isCustomer ? "customer" : "coach");
  const queryClient = useQueryClient();

  const hasCoachAccount = userInfo?.hasCoachAccount ?? false;
  const hasCustomerAccount = userInfo?.hasCustomerAccount ?? false;

  const handleContextChange = (value: string | number) => {
    const newContext = value as UserContext;

    if (newContext === currentUserContext) {
      return;
    }

    if (newContext === "coach") {
      if (hasCoachAccount) {
        const coachToken = userInfo?.coachToken;

        if (coachToken) {
          dispatch(setContextSwitching(true));
          dispatch(
            switchUserContext({
              newToken: coachToken,
              newIsCustomer: false,
            })
          );
          dispatch(setUserContext("coach"));
          setTimeout(() => {
            dispatch(setContextSwitching(false));
          }, 1000);
          queryClient.clear();
          navigate("/home");
        } else {
          setShowModal(true);
        }
      } else {
        setShowModal(true);
      }
    } else if (newContext === "customer") {
      if (hasCustomerAccount) {
        const customerToken = userInfo?.customerToken;

        if (customerToken) {
          dispatch(setContextSwitching(true));
          dispatch(
            switchUserContext({
              newToken: customerToken,
              newIsCustomer: true,
            })
          );
          dispatch(setUserContext("customer"));
          setTimeout(() => {
            dispatch(setContextSwitching(false));
          }, 1000);
          queryClient.clear();
          navigate("/customer/home");
        }
      } else {
        setShowCustomerModal(true);
      }
    }
  };

  return (
    <>
      <StyledContextSwitch>
        <h3 className="account-switch-heading">ACCOUNT SWITCH</h3>
        <Segmented
          value={currentUserContext}
          onChange={handleContextChange}
          options={[
            {
              label: (
                <span style={{display: "flex", alignItems: "center", gap: "0.5rem"}}>
                  <UserOutlined />
                  <span>Coach</span>
                </span>
              ),
              value: "coach",
            },
            {
              label: (
                <span style={{display: "flex", alignItems: "center", gap: "0.5rem"}}>
                  <ShoppingCartOutlined
                    style={{
                      fontSize: "16px",
                    }}
                  />
                  <span>Customer</span>
                </span>
              ),
              value: "customer",
            },
          ]}
          block
          size="small"
        />
      </StyledContextSwitch>
      <CreateCoachAccountModal visible={showModal} onCancel={() => setShowModal(false)} />
      <CreateCustomerAccountModal
        visible={showCustomerModal}
        onCancel={() => setShowCustomerModal(false)}
      />
    </>
  );
};

export {ContextSwitch};
